import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface UserNotification {
  id: string;
  user_id: string;
  category: string;
  title: string;
  body: string | null;
  link_url: string | null;
  icon: string | null;
  team_id: string | null;
  participant_id: string | null;
  related_id: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

interface UseUserNotificationsOptions {
  limit?: number;
}

interface UseUserNotificationsResult {
  notifications: UserNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * Per-user in-app notification inbox. Powers the header bell + /notifications.
 * Backed by `user_notifications` (RLS-scoped to auth.uid()) and listens for
 * realtime INSERT/UPDATE so the bell updates without a refresh.
 */
export function useUserNotifications(
  options: UseUserNotificationsOptions = {}
): UseUserNotificationsResult {
  const { limit = 50 } = options;
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id ?? null;

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const [listRes, countRes] = await Promise.all([
      supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('user_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null),
    ]);

    if (listRes.error) {
      setError(listRes.error.message);
      setLoading(false);
      return;
    }

    setNotifications((listRes.data as UserNotification[] | null) ?? []);
    setUnreadCount(countRes.count ?? 0);
    setLoading(false);
  }, [user, limit]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`user_notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as UserNotification;
            setNotifications((prev) => {
              if (prev.some((n) => n.id === row.id)) return prev;
              return [row, ...prev].slice(0, limit);
            });
            if (!row.read_at) {
              setUnreadCount((c) => c + 1);
            }
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as UserNotification;
            const old = payload.old as UserNotification;
            setNotifications((prev) =>
              prev.map((n) => (n.id === row.id ? { ...n, ...row } : n))
            );
            if (!old.read_at && row.read_at) {
              setUnreadCount((c) => Math.max(0, c - 1));
            } else if (old.read_at && !row.read_at) {
              setUnreadCount((c) => c + 1);
            }
          } else if (payload.eventType === 'DELETE') {
            const old = payload.old as UserNotification;
            setNotifications((prev) => prev.filter((n) => n.id !== old.id));
            if (!old.read_at) {
              setUnreadCount((c) => Math.max(0, c - 1));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, limit]);

  const markRead = useCallback(
    async (id: string) => {
      if (!user) return;
      const target = notifications.find((n) => n.id === id);
      if (target?.read_at) return;
      const nowIso = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: nowIso } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      const { error: rpcErr } = await supabase.rpc('mark_user_notification_read', {
        p_id: id,
      });
      if (rpcErr) {
        console.error('mark_user_notification_read failed:', rpcErr);
        await fetchNotifications();
      }
    },
    [user, notifications, fetchNotifications]
  );

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const nowIso = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: nowIso }))
    );
    setUnreadCount(0);
    const { error: rpcErr } = await supabase.rpc(
      'mark_all_user_notifications_read'
    );
    if (rpcErr) {
      console.error('mark_all_user_notifications_read failed:', rpcErr);
      await fetchNotifications();
    }
  }, [user, fetchNotifications]);

  return useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      refresh: fetchNotifications,
      markRead,
      markAllRead,
    }),
    [notifications, unreadCount, loading, error, fetchNotifications, markRead, markAllRead]
  );
}
